import { useCallback, useMemo } from 'react';
import {
    confirmModal, hideAppSpinner, showAppNotification, showAppSpinner,
} from '@rainbow-modules/app';
import { useOpenModal } from '@rainbow-modules/hooks';
import Trash from 'components/icons/trash';
import ModalBody from 'components/ModalBody';
import { Externalautomation } from 'data/firestore/agent/externalautomation/types';
import useHttpMutation from 'data/firestore/useHttpMutation';

const mapDataToFormValues = (data: Externalautomation) => data;

const mapFormValuesToData = (values: Record<string, unknown>) => values;

const useManageExternalAutomations = ({
    agentId,
    modalId,
    onCreated = () => {},
    onDeleted = () => {},
} : {
    agentId: string;
    modalId: string;
    onCreated?: (externalAutomation: Externalautomation) => void
    onDeleted?: () => void;
}) => {
    const [openForm, closeForm] = useOpenModal(modalId);

    const { mutateAsync: createExternalAutomation } = useHttpMutation({
        method: 'POST',
        pathname: `/agents/${agentId}/external-automations`,
        onSuccess: closeForm,
    });

    const { mutateAsync: updateExternalAutomation } = useHttpMutation({
        method: 'PATCH',
        onSuccess: closeForm,
    });

    const { mutateAsync: deleteExternalAutomation } = useHttpMutation({
        method: 'DELETE',
    });

    const handleCreateExternalAutomation = useCallback(
        (initialValues: Record<string, unknown> = {}) => openForm({
            title: 'Create New External Automation',
            submitButtonLabel: 'Add',
            externalAutomationData: initialValues,
            onSubmit: async (values: Record<string, unknown>) => {
                showAppSpinner();
                try {
                    const externalAutomation = await createExternalAutomation({
                        body: mapFormValuesToData(values),
                    }) as Externalautomation;
                    showAppNotification({
                        title: 'Success',
                        description: 'External automation added successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                    onCreated(externalAutomation);
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [createExternalAutomation, onCreated, openForm],
    );

    const handleEditExternalAutomation = useCallback(
        (data: Externalautomation) => openForm({
            disabledFields: [],
            title: 'Edit External Automation',
            mode: 'edit',
            submitButtonLabel: 'Update',
            externalAutomationData: mapDataToFormValues(data),
            onSubmit: async (values: Record<string, unknown>) => {
                showAppSpinner();
                try {
                    await updateExternalAutomation({
                        pathname: `/agents/${agentId}/external-automations/${data.id}`,
                        body: mapFormValuesToData(values),
                    });
                    showAppNotification({
                        title: 'Success',
                        description: 'External automation updated successfully.',
                        icon: 'success',
                        timeout: 5000,
                    });
                } catch (error) {
                    const message = (error as any)?.message || 'Something went wrong. Please try again.';
                    showAppNotification({
                        title: 'Error',
                        description: message,
                        icon: 'error',
                        timeout: 5000,
                    });
                }
                hideAppSpinner();
            },
        }),
        [openForm, agentId, updateExternalAutomation],
    );

    const handleDeleteExternalAutomation = useCallback(
        async (externalAutomationId: string) => {
            const result = await confirmModal({
                header: '',
                variant: 'destructive',
                question: (
                    <ModalBody
                        variant="destructive"
                        icon={<Trash />}
                        title="Remove external automation?"
                        description="Once this action is confirmed, it cannot be undone."
                    />
                ),
                borderRadius: 'semi-square',
                okButtonLabel: 'Remove',
                cancelButtonLabel: 'Cancel',
            });

            if (!result) {
                return;
            }

            showAppSpinner();
            try {
                await deleteExternalAutomation({
                    pathname: `/agents/${agentId}/external-automations/${externalAutomationId}`,
                });
                showAppNotification({
                    title: 'Success',
                    description: 'External automation deleted successfully.',
                    icon: 'success',
                    timeout: 5000,
                });
                onDeleted();
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            hideAppSpinner();
        },
        [deleteExternalAutomation, agentId, onDeleted],
    );

    return useMemo(
        () => ({
            create: handleCreateExternalAutomation,
            edit: handleEditExternalAutomation,
            remove: handleDeleteExternalAutomation,
            closeForm,
        }),
        [
            closeForm,
            handleCreateExternalAutomation,
            handleDeleteExternalAutomation,
            handleEditExternalAutomation,
        ],
    );
};

export default useManageExternalAutomations;
